import { DateTime, Effect, Option, Schema, SchemaIssue, SchemaTransformation } from "effect";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_REGEX = /^\d{4}-\d{2}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const requiredString = (message: string) =>
  Schema.String.pipe(Schema.check(Schema.isMinLength(1, { message })));

export const stringMax = (max: number, message: string) =>
  Schema.String.pipe(Schema.check(Schema.isMaxLength(max, { message })));

export const requiredStringMax = (minMessage: string, max: number, maxMessage: string) =>
  Schema.String.pipe(
    Schema.check(Schema.isMinLength(1, { message: minMessage })),
    Schema.check(Schema.isMaxLength(max, { message: maxMessage })),
  );

export const uuidString = (message = "无效的 ID") =>
  Schema.String.pipe(Schema.check(Schema.isPattern(UUID_REGEX, { message })));

export const dateString = (message = "日期格式必须为 YYYY-MM-DD") =>
  Schema.String.pipe(Schema.check(Schema.isPattern(DATE_REGEX, { message })));

export const monthString = (message = "格式必须为 YYYY-MM") =>
  Schema.String.pipe(Schema.check(Schema.isPattern(MONTH_REGEX, { message })));

export const emailString = (message = "请输入有效的邮箱地址") =>
  Schema.String.pipe(Schema.check(Schema.isPattern(EMAIL_REGEX, { message })));

export const urlString = (message = "请输入有效的 URL") =>
  Schema.String.pipe(
    Schema.check(
      Schema.makeFilter((value) => URL.canParse(value), {
        message,
      }),
    ),
  );

export const positiveInteger = (message = "必须是正整数") =>
  Schema.Finite.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThan(0, { message })),
  );

export const nonNegativeInteger = (message = "不能为负") =>
  Schema.Finite.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(0, { message })),
  );

export const stringArrayOrNull = Schema.NullOr(Schema.Array(Schema.String));

export const emptyOr = <A, I>(schema: Schema.ConstraintCodec<A, I>) =>
  Schema.Union([schema, Schema.Literals([""])]);

export const coerceDate = (message = "日期无效") =>
  Schema.Union([Schema.Date, Schema.String, Schema.Finite])
    .pipe(
      Schema.decodeTo(
        Schema.DateValid,
        SchemaTransformation.transformOrFail({
          decode: (value) => {
            const parsed = DateTime.make(value);
            return Option.isSome(parsed)
              ? Effect.succeed(DateTime.toDateUtc(parsed.value))
              : Effect.fail(new SchemaIssue.InvalidValue(Option.some(value), { message }));
          },
          encode: Effect.succeed,
        }),
      ),
    )
    .annotate({ message });

export function normalizeNulls<T extends Record<string, unknown>>(
  obj: T,
): { [K in keyof T]: T[K] extends null ? undefined : T[K] } {
  type ResultType = { [K in keyof T]: T[K] extends null ? undefined : T[K] };
  const result = {} as ResultType;
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      result[key] = (obj[key] === null ? undefined : obj[key]) as ResultType[typeof key];
    }
  }
  return result;
}

export function normalizeUndefineds<T extends Record<string, unknown>>(
  obj: T,
): { [K in keyof T]: T[K] extends undefined ? null : T[K] } {
  type ResultType = { [K in keyof T]: T[K] extends undefined ? null : T[K] };
  const result = {} as ResultType;
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      result[key] = (obj[key] === undefined ? null : obj[key]) as ResultType[typeof key];
    }
  }
  return result;
}

export function normalizeArrayNulls<T extends Record<string, unknown>>(
  arr: T[],
): { [K in keyof T]: T[K] extends null ? undefined : T[K] }[] {
  return arr.map(normalizeNulls);
}
